import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { JupiterFormPanelMeta } from '../types';
import { DirectivesModule } from '@shared/directives/directives.module';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { WidgetsModule } from '@shared/components/widgets/widgets.module';
import { TsButtonModule, TSIconModule, TsPipesModule } from 'tsui';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { PanelContainerComponent } from '../@shared/panel-container/panel-container.component';
import { PanelHeaderComponent } from '../@shared/panel-header/panel-header.component';
import { JupiterPanelComponent } from '../jupiter-panel.component';

@Component({
  selector: 'jupiter-form-panel',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatLegacyMenuModule,
    MatLegacyFormFieldModule,
    ReactiveFormsModule,
    DragDropModule,
    DirectivesModule,
    TsButtonModule,
    FormFieldsModule,
    WidgetsModule,
    MatLegacyInputModule,
    PanelContainerComponent,
    PanelHeaderComponent,
    TsPipesModule,
    TSIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPanelComponent extends JupiterPanelComponent<JupiterFormPanelMeta> implements OnInit {
  private touchedFields = new Set<string>();

  ngOnInit(): void {
    this.panelDef = { ...this.panelDef };
    this.panelDef.meta.fields = this.panelDef.meta.fields.map((row) =>
      row.filter((fieldName) => this.lookupField(fieldName)),
    );

    super.ngOnInit();
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  manageField(
    mode: 'first' | 'addInColumn' | 'addInNewRow' | 'remove',
    data?: { rowIndex: number; colIndex?: number },
  ): void {
    const [firstField] = this.fieldDefs;

    switch (mode) {
      case 'first':
        this.panelDef.meta.fields = [[firstField.name]];
        break;
      case 'addInColumn':
        this.panelDef.meta.fields[data.rowIndex] = [...this.panelDef.meta.fields[data.rowIndex], firstField.name];
        break;
      case 'addInNewRow':
        this.panelDef.meta.fields = [...this.panelDef.meta.fields, [firstField.name]];
        break;
      case 'remove':
        this.panelDef.meta.fields[data.rowIndex] = this.panelDef.meta.fields[data.rowIndex].filter(
          (_, index) => index !== data.colIndex,
        );
        this.panelDef.meta.fields = [...this.panelDef.meta.fields];
        break;
    }
  }

  update(data: any): void {
    this.touchModel(data);
    this.touched.next(true);
    this.model = { ...this.model, ...data };
  }

  onStartEditLayout(): void {
    if (this.editMode.value && this.panelDef.meta.fields?.every((arr) => arr.length === 0)) {
      this.manageField('first');
    }
    super.onStartEditLayout();
  }

  onSaveLayout(): void {
    this.panelDef = {
      ...this.panelDef,
      meta: { ...this.panelDef.meta, fields: this.panelDef.meta.fields.filter((row) => row.length) },
    };
    super.onSaveLayout();
  }

  hasFields(fields: string[][]): boolean {
    return fields?.every((arr) => arr.length === 0) === false;
  }

  saveChanges(): void {
    this.dataUpdated.emit({
      model: this.model,
      origin: this.panelDef,
      data: { touchedFields: [...this.touchedFields] },
    });
    this.editMode.next(false);
  }

  undoChanges(): void {
    this.touchedFields.clear();
    super.discardChanges();
  }

  private touchModel(data: any): void {
    const [fieldName] = Object.keys(data ?? {});
    this.touchedFields.add(fieldName);
  }
}
